import { SyntheticEvent, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import CircularProgress from '@mui/material/CircularProgress'
import TextField from '@mui/material/TextField'
import {
  usePlacesAutocomplete,
  type PlacePredictionItem,
  type ResolvedPlace
} from '../../hooks/usePlacesAutocomplete'

export type SuburbAutocompleteProps = {
  value: string
  onChange: (value: string) => void
  onPlaceSelect: (place: ResolvedPlace) => void
  onPlaceClear: () => void
  error?: boolean
  helperText?: string
  disabled?: boolean
}

const SuburbAutocomplete = ({
  value,
  onChange,
  onPlaceSelect,
  onPlaceClear,
  error,
  helperText,
  disabled
}: SuburbAutocompleteProps) => {
  const {
    apiState,
    predictions,
    isLoadingPredictions,
    setInput,
    selectPrediction,
    clearPredictions
  } = usePlacesAutocomplete({
    apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''
  })

  const [open, setOpen] = useState(false)

  // Keep popup open when there's meaningful input (>= 3 chars)
  // and we either have predictions, are loading, or got no results
  const shouldShowPopup = open && value.length >= 3

  const handleInputChange = (
    _event: SyntheticEvent,
    newValue: string,
    reason: string
  ) => {
    if (reason === 'clear' || newValue === '') {
      clearPredictions()
      onChange('')
      onPlaceClear()
      return
    }

    onChange(newValue)
    setInput(newValue)
  }

  const handleChange = async (
    _event: SyntheticEvent,
    newValue: string | PlacePredictionItem | null
  ) => {
    if (newValue === null || newValue === '') {
      clearPredictions()
      onChange('')
      onPlaceClear()
      return
    }

    if (typeof newValue === 'string') {
      onChange(newValue)
      return
    }

    // User selected a prediction from the list
    onChange(newValue.fullText)
    try {
      const resolved = await selectPrediction(newValue)
      onPlaceSelect(resolved)
    } catch {
      // Resolution error is handled by the hook's state
    }
  }

  // Fallback to plain TextField when API fails to load
  if (apiState === 'error') {
    return (
      <TextField
        value={value}
        onChange={e => onChange(e.target.value)}
        label='Suburb'
        placeholder='Start typing a suburb...'
        fullWidth
        error={error}
        helperText={helperText}
        disabled={disabled}
      />
    )
  }

  // Show "No results found" state: input has meaningful text but no predictions and not loading
  const showNoResults =
    value.length >= 3 && predictions.length === 0 && !isLoadingPredictions

  return (
    <Autocomplete
      freeSolo
      open={shouldShowPopup}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterOptions={x => x}
      options={predictions}
      getOptionLabel={option =>
        typeof option === 'string' ? option : option.fullText
      }
      loading={isLoadingPredictions || showNoResults}
      loadingText={showNoResults ? 'No results found' : 'Loading...'}
      noOptionsText='No results found'
      inputValue={value}
      onInputChange={handleInputChange}
      onChange={handleChange}
      disabled={disabled || apiState === 'loading'}
      renderInput={params => (
        <TextField
          {...params}
          label='Suburb'
          placeholder='Start typing a suburb...'
          fullWidth
          error={error}
          helperText={helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingPredictions ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  )
}

export default SuburbAutocomplete
